import { useCallback, useEffect, useState } from "react";
import {
  Ban, CircleCheck, KeyRound, ListChecks, Pencil, ShieldCheck, Trash2, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PermissionMatrix, { emptyMatrix } from "@/components/admin/PermissionMatrix";

const fmtIST = (ts) =>
  ts
    ? new Date(ts).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const ROLE_LABEL = { super_admin: "Super Admin", full_admin: "Full Admin", custom_admin: "Custom Admin" };
function methodLabel(m) {
  if (m === "password") return "Password";
  if (m === "google") return "Google";
  if (m === "both") return "Password + Google";
  return m;
}
const EMPTY_FORM = { name: "", email: "", auth_method: "password", role: "custom_admin", password: "", permissions: emptyMatrix() };

function RoleBadge({ role }) {
  const cls =
    role === "super_admin" ? "bg-gold/20 text-yellow-800 border-gold/40"
    : role === "full_admin" ? "bg-navy/10 text-navy border-navy/20"
    : "bg-secondary text-foreground/70 border-border";
  return <Badge variant="outline" className={cls} data-testid={`role-badge-${role}`}>{ROLE_LABEL[role] || role}</Badge>;
}

function StatusBadge({ status }) {
  return status === "disabled" ? (
    <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700" data-testid="status-disabled">Disabled</Badge>
  ) : (
    <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700" data-testid="status-active">Active</Badge>
  );
}

function AccessTypePicker({ role, setRole, perms, setPerms, idPrefix }) {
  return (
    <div className="space-y-3">
      <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={`${idPrefix}-role-picker`}>
        <Label htmlFor={`${idPrefix}-full`} className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${role === "full_admin" ? "border-gold bg-gold/10" : "border-border"}`}>
          <RadioGroupItem value="full_admin" id={`${idPrefix}-full`} data-testid={`${idPrefix}-role-full`} />
          <span>
            <span className="block text-sm font-semibold">Full Access</span>
            <span className="block text-xs text-muted-foreground">All admin features except Access Management</span>
          </span>
        </Label>
        <Label htmlFor={`${idPrefix}-custom`} className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${role === "custom_admin" ? "border-gold bg-gold/10" : "border-border"}`}>
          <RadioGroupItem value="custom_admin" id={`${idPrefix}-custom`} data-testid={`${idPrefix}-role-custom`} />
          <span>
            <span className="block text-sm font-semibold">Custom Access</span>
            <span className="block text-xs text-muted-foreground">Only the modules and actions you select</span>
          </span>
        </Label>
      </RadioGroup>
      {role === "custom_admin" && <PermissionMatrix value={perms} onChange={setPerms} idPrefix={idPrefix} />}
    </div>
  );
}

export default function AccessManagement() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null);
  const [permTarget, setPermTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokePassword, setRevokePassword] = useState("");

  const load = useCallback(async () => {
    try {
      const [ov, ad] = await Promise.all([
        api.get("/admin/access/overview"),
        api.get("/admin/access/admins"),
      ]);
      setOverview(ov.data);
      setAdmins(ad.data.items);
    } catch (e) {
      toast.error(apiError(e, "Could not load access data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "super_admin") load();
  }, [load, user]);

  if (user && user.role !== "super_admin") {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-8 text-center" data-testid="access-denied-page">
        <ShieldCheck className="mx-auto h-8 w-8 text-red-400" />
        <h2 className="mt-3 font-heading text-lg font-bold text-navy">Super Admin only</h2>
        <p className="mt-1 text-sm text-foreground/70">Access Management is restricted to the Super Admin.</p>
      </div>
    );
  }

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      await load();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitAdd = async () => {
    const ok = await run(
      () => api.post("/admin/access/admins", {
        name: form.name, email: form.email, auth_method: form.auth_method,
        role: form.role, permissions: form.role === "custom_admin" ? form.permissions : {},
        password: form.password,
      }),
      "Administrator added"
    );
    if (ok) { setAddOpen(false); setForm(EMPTY_FORM); }
  };

  const submitEdit = async () => {
    const ok = await run(
      () => api.put(`/admin/access/admins/${editTarget.id}`, {
        name: editTarget.name, auth_method: editTarget.auth_method, password: editTarget.password || "",
      }),
      "Administrator updated"
    );
    if (ok) setEditTarget(null);
  };

  const submitRole = async () => {
    const ok = await run(
      () => api.put(`/admin/access/admins/${roleTarget.id}/role`, {
        role: roleTarget.role,
        permissions: roleTarget.role === "custom_admin" ? roleTarget.permissions : {},
      }),
      "Role updated"
    );
    if (ok) setRoleTarget(null);
  };

  const submitPerms = async () => {
    const ok = await run(
      () => api.put(`/admin/access/admins/${permTarget.id}/permissions`, { permissions: permTarget.permissions }),
      "Permissions updated"
    );
    if (ok) setPermTarget(null);
  };

  const submitToggle = async () => {
    const action = toggleTarget.status === "disabled" ? "enable" : "disable";
    const ok = await run(
      () => api.post(`/admin/access/admins/${toggleTarget.id}/${action}`),
      action === "disable" ? "Administrator disabled — access revoked immediately" : "Administrator enabled"
    );
    if (ok) setToggleTarget(null);
  };

  const submitRevoke = async () => {
    const ok = await run(
      () => api.delete(`/admin/access/admins/${revokeTarget.id}`, { data: { password: revokePassword } }),
      "Access permanently revoked"
    );
    if (ok) { setRevokeTarget(null); setRevokePassword(""); }
  };

  const stats = [
    ["Total Admins", overview?.total, "access-stat-total"],
    ["Active", overview?.active, "access-stat-active"],
    ["Disabled", overview?.disabled, "access-stat-disabled"],
    ["Full Admins", overview?.full_admins, "access-stat-full"],
    ["Custom Admins", overview?.custom_admins, "access-stat-custom"],
  ];

  return (
    <div className="space-y-8" data-testid="access-management-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Access Management</h1>
          <p className="text-sm text-muted-foreground">Control who can access the Admin Panel and what they can do.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-gold text-navy hover:bg-gold-dark" data-testid="access-add-admin-btn">
          <UserPlus className="mr-2 h-4 w-4" /> Add Admin
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {stats.map(([label, val, tid]) => (
          <div key={tid} className="rounded-lg border border-border bg-white p-4" data-testid={tid}>
            <p className="text-2xl font-bold text-navy">{loading ? "…" : val ?? 0}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm" data-testid="access-admins-table">
          <thead>
            <tr className="border-b border-border bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Auth</th>
              <th className="px-4 py-3">Permissions</th>
              <th className="px-4 py-3">Added</th>
              <th className="px-4 py-3">Last Login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} className="border-b border-border/60 last:border-0" data-testid={`access-row-${a.email}`}>
                <td className="px-4 py-3 font-medium text-navy">
                  {a.name || "—"}
                  {a.role === "super_admin" && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-gold-dark" data-testid="super-admin-protected">
                      <ShieldCheck className="h-3 w-3" /> Protected
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-foreground/80">{a.email}</td>
                <td className="px-4 py-3"><RoleBadge role={a.role} /></td>
                <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                <td className="px-4 py-3 text-foreground/70">{methodLabel(a.auth_method)}</td>
                <td className="max-w-56 truncate px-4 py-3 text-xs text-foreground/70" title={a.permissions_summary}>{a.permissions_summary}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-foreground/70">
                  {fmtIST(a.created_at)}
                  {a.added_by && a.added_by !== "system" && <span className="block text-muted-foreground">by {a.added_by}</span>}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-foreground/70">
                  {fmtIST(a.last_login_at)}
                  {a.last_login_method && <span className="block text-muted-foreground">{a.last_login_method}</span>}
                </td>
                <td className="px-4 py-3">
                  {a.role !== "super_admin" && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title="Edit" data-testid={`access-edit-${a.id}`}
                        onClick={() => setEditTarget({ ...a, password: "" })}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Change Role" data-testid={`access-role-${a.id}`}
                        onClick={() => setRoleTarget({ ...a, permissions: a.permissions?.dashboard ? a.permissions : emptyMatrix() })}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      {a.role === "custom_admin" && (
                        <Button variant="ghost" size="icon" title="Manage Permissions" data-testid={`access-perms-${a.id}`}
                          onClick={() => setPermTarget({ ...a })}>
                          <ListChecks className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" title={a.status === "disabled" ? "Enable" : "Disable"} data-testid={`access-toggle-${a.id}`}
                        onClick={() => setToggleTarget(a)}>
                        {a.status === "disabled" ? <CircleCheck className="h-4 w-4 text-green-600" /> : <Ban className="h-4 w-4 text-amber-600" />}
                      </Button>
                      <Button variant="ghost" size="icon" title="Revoke Access" data-testid={`access-revoke-${a.id}`}
                        onClick={() => { setRevokeTarget(a); setRevokePassword(""); }}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-white p-4" data-testid="access-recent-logins">
          <h3 className="mb-3 font-heading text-sm font-bold text-navy">Recent Login Activity</h3>
          <ul className="space-y-2 text-xs text-foreground/80">
            {(overview?.recent_logins || []).map((l) => (
              <li key={l.id} className="flex justify-between gap-2">
                <span className="truncate">{l.user_email} — {l.detail}</span>
                <span className="shrink-0 text-muted-foreground">{fmtIST(l.created_at)}</span>
              </li>
            ))}
            {!overview?.recent_logins?.length && <li className="text-muted-foreground">No login activity yet.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-white p-4" data-testid="access-recent-security">
          <h3 className="mb-3 font-heading text-sm font-bold text-navy">Recent Security Events</h3>
          <ul className="space-y-2 text-xs text-foreground/80">
            {(overview?.recent_security || []).map((l) => (
              <li key={l.id} className="flex justify-between gap-2">
                <span className="truncate">{l.user_email} — {l.action}: {l.record}</span>
                <span className="shrink-0 text-muted-foreground">{fmtIST(l.created_at)}</span>
              </li>
            ))}
            {!overview?.recent_security?.length && <li className="text-muted-foreground">No security events yet.</li>}
          </ul>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="access-add-dialog">
          <DialogHeader><DialogTitle>Add Administrator</DialogTitle><DialogDescription>Authorise a new person for Admin Panel access and choose exactly what they can do.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="add-name">Full Name</Label>
                <Input id="add-name" className="mt-1.5" value={form.name} data-testid="access-add-name"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="add-email">Email Address</Label>
                <Input id="add-email" type="email" className="mt-1.5" value={form.email} data-testid="access-add-email"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Authentication Method</Label>
              <Select value={form.auth_method} onValueChange={(v) => setForm({ ...form, auth_method: v })}>
                <SelectTrigger className="mt-1.5" data-testid="access-add-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="password" data-testid="access-add-method-password">Username / Password</SelectItem>
                  <SelectItem value="google" data-testid="access-add-method-google">Google Sign-In (authorise this email)</SelectItem>
                  <SelectItem value="both" data-testid="access-add-method-both">Both</SelectItem>
                </SelectContent>
              </Select>
              {form.auth_method !== "password" && (
                <p className="mt-1 text-xs text-muted-foreground">
                  This Google email will be authorised for Admin Panel access. Unauthorised Google accounts are denied automatically.
                </p>
              )}
            </div>
            {form.auth_method !== "google" && (
              <div>
                <Label htmlFor="add-password">Temporary Password (share it with them manually)</Label>
                <Input id="add-password" type="text" className="mt-1.5" value={form.password} data-testid="access-add-password"
                  placeholder="Minimum 8 characters" onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
            <div>
              <Label className="mb-2 block">Access Level</Label>
              <AccessTypePicker role={form.role} setRole={(r) => setForm({ ...form, role: r })}
                perms={form.permissions} setPerms={(p) => setForm({ ...form, permissions: p })} idPrefix="add" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="access-add-cancel">Cancel</Button>
            <Button onClick={submitAdd} disabled={busy} className="bg-gold text-navy hover:bg-gold-dark" data-testid="access-add-submit">
              {busy ? "Saving…" : "Add Administrator"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="access-edit-dialog">
          <DialogHeader><DialogTitle>Edit Administrator — {editTarget?.email}</DialogTitle><DialogDescription>Update the account name, sign-in method, or reset the password.</DialogDescription></DialogHeader>
          {editTarget && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input id="edit-name" className="mt-1.5" value={editTarget.name} data-testid="access-edit-name-input"
                  onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })} />
              </div>
              <div>
                <Label>Authentication Method</Label>
                <Select value={editTarget.auth_method} onValueChange={(v) => setEditTarget({ ...editTarget, auth_method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="access-edit-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="password">Username / Password</SelectItem>
                    <SelectItem value="google">Google Sign-In</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-password">Reset Password (optional)</Label>
                <Input id="edit-password" type="text" className="mt-1.5" value={editTarget.password} data-testid="access-edit-password"
                  placeholder="Leave blank to keep current password"
                  onChange={(e) => setEditTarget({ ...editTarget, password: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="access-edit-cancel">Cancel</Button>
            <Button onClick={submitEdit} disabled={busy} className="bg-gold text-navy hover:bg-gold-dark" data-testid="access-edit-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(roleTarget)} onOpenChange={() => setRoleTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="access-role-dialog">
          <DialogHeader><DialogTitle>Change Role — {roleTarget?.email}</DialogTitle><DialogDescription>Switch between Full Access and Custom Access with a permission matrix.</DialogDescription></DialogHeader>
          {roleTarget && (
            <AccessTypePicker role={roleTarget.role} setRole={(r) => setRoleTarget({ ...roleTarget, role: r })}
              perms={roleTarget.permissions} setPerms={(p) => setRoleTarget({ ...roleTarget, permissions: p })} idPrefix="role" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleTarget(null)} data-testid="access-role-cancel">Cancel</Button>
            <Button onClick={submitRole} disabled={busy} className="bg-gold text-navy hover:bg-gold-dark" data-testid="access-role-submit">Change Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(permTarget)} onOpenChange={() => setPermTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="access-perms-dialog">
          <DialogHeader><DialogTitle>Manage Permissions — {permTarget?.email}</DialogTitle><DialogDescription>Select exactly which modules and actions this Custom Admin may use.</DialogDescription></DialogHeader>
          {permTarget && (
            <PermissionMatrix value={permTarget.permissions} idPrefix="manage"
              onChange={(p) => setPermTarget({ ...permTarget, permissions: p })} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermTarget(null)} data-testid="access-perms-cancel">Cancel</Button>
            <Button onClick={submitPerms} disabled={busy} className="bg-gold text-navy hover:bg-gold-dark" data-testid="access-perms-submit">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toggleTarget)} onOpenChange={() => setToggleTarget(null)}>
        <AlertDialogContent data-testid="access-toggle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toggleTarget?.status === "disabled" ? "Enable" : "Disable"} {toggleTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toggleTarget?.status === "disabled"
                ? "This restores Admin Panel access for this account immediately."
                : "This immediately blocks Admin Panel access and ends their active sessions. The account stays in the system and can be re-enabled later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="access-toggle-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitToggle} disabled={busy} data-testid="access-toggle-confirm">
              {toggleTarget?.status === "disabled" ? "Enable Access" : "Disable Access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(revokeTarget)} onOpenChange={() => setRevokeTarget(null)}>
        <AlertDialogContent data-testid="access-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently revoke access for {revokeTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the account and all its sessions. To confirm, re-enter your Super Admin password.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="revoke-password">Your Super Admin password</Label>
            <Input id="revoke-password" type="password" className="mt-1.5" value={revokePassword}
              data-testid="access-revoke-password" onChange={(e) => setRevokePassword(e.target.value)} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="access-revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitRevoke} disabled={busy} className="bg-red-600 text-white hover:bg-red-700" data-testid="access-revoke-confirm">
              Revoke Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
